/**
 * Donate feature configuration.
 *
 * Controls visibility of the donate button per UI locale.
 * Set to `true` to show the donate button for a locale, `false` to hide it.
 *
 * Examples:
 *   { vi: true, en: false }  — show only in Vietnamese
 *   { vi: true, en: true }   — show in both languages
 *   { vi: false, en: false }  — hide in all languages
 */
export const DONATE_VISIBILITY: Record<string, boolean> = {
  vi: true,
  en: false,
};

/**
 * Path to the donate QR code image per UI locale (relative to public/).
 *
 * Each locale can have its own QR code image. To change a QR code:
 * 1. Replace the image file at the specified path, or
 * 2. Update the path in this map to point to a different file.
 *
 * Supported formats: PNG, JPG, SVG, WebP.
 *
 * Examples:
 *   { vi: '/images/donate-qr-vi.png', en: '/images/donate-qr-en.png' }
 *   { vi: '/images/donate-qr.svg', en: '/images/donate-qr.svg' }  // Same QR for all
 */
export const DONATE_QR_IMAGE_PATHS: Record<string, string> = {
  vi: '/images/donate-qr-square-vn.jpg',
  en: '/images/donate-qr-en.svg',
};

/**
 * Get the QR code image path for a specific locale.
 * Falls back to the first available path if locale not found.
 *
 * @param locale - The UI locale code (e.g., 'vi', 'en')
 * @returns The QR code image path for the locale
 */
export function getDonateQrImagePath(locale: string): string {
  return DONATE_QR_IMAGE_PATHS[locale] || Object.values(DONATE_QR_IMAGE_PATHS)[0] || '/images/donate-qr.svg';
}
